import { CreateRebateSettings, RebateSettingsDto } from '@/api/Trading-Accounts/RebateSettingsDto'
import { IntroducingBrokerCommissionPlanGroupDto } from '@/api/Trading-Accounts/Types/IntroducingBrokerCommissionPlanGroupDto'
import { IntroducingBrokerLoyaltyDto } from '@/api/Trading-Accounts/Types/IntroducingBrokerLoyaltyDto'
import { IntroducingBrokerMultiTierDetails } from '@/api/Trading-Accounts/Types/IntroducingBrokerMultiTierDetails'
import { MultiTierNetworkDto } from '@/api/Trading-Accounts/Types/MultiTierNetworkDto'
import {
  CreateRebatePolicies,
  RebatePoliciesDto,
} from '@/api/Trading-Accounts/Types/RebatePoliciesDto'
import { UpdateIntroducingBrokerMultiTierSubAccountFormValues } from '@/routes/Records/IB/IntroducingBrokerMTNetworkSubAccountEditForm'
import { AddEditIntroducingBrokerCommissionPlanGroupDtoFormValues } from '@/routes/Records/IB/IntroducingBrokerRebateFormModal'
import { AuditLog } from 'api/Clients/Types/AuditLog'
import { DealDto } from 'api/Clients/Types/DealDto'
import { RoleSecuredProperties } from 'api/Clients/Types/RoleSecuredProperties'
import { SecuredPropertiesLabels } from 'api/Clients/Types/SecuredPropertiesLabels'
import { TradingAccountDto } from 'api/Clients/Types/TradingAccount'
import { NameDto } from 'api/Common/Types'
import { PageData } from 'api/Pagination/Types'
import { CreateIntroducingBrokerProfile } from 'api/Trading-Accounts/Types/CreateIntroducingBrokerProfile'
import { IntroducingBrokerBasicDto } from 'api/Trading-Accounts/Types/IntroducingBrokerBasicDto'
import { IntroducingBrokerCalculationStatus } from 'api/Trading-Accounts/Types/IntroducingBrokerCalculationStatus'
import {
  Instrument,
  IntroducingBroker,
  IntroducingBrokerDetailedProfileDto,
  StandardCommission,
  StandardCommissionDto,
} from 'api/Trading-Accounts/Types/IntroducingBrokerDetailedProfileDto'
import {
  AdjustNotionalValue,
  IntroducingBrokerDetailsDto,
} from 'api/Trading-Accounts/Types/IntroducingBrokerDetailsDto'
import { IntroducingBrokerDto } from 'api/Trading-Accounts/Types/IntroducingBrokerDto'
import { IntroducingBrokerProfileAccountType } from 'api/Trading-Accounts/Types/IntroducingBrokerProfileAccountType'
import { IntroducingBrokerProfileDto } from 'api/Trading-Accounts/Types/IntroducingBrokerProfileDto'
import {
  CreateRebateGroup,
  IBIndividualRebate,
  IBIndividualRebateData,
  IntroducingBrokerRebate,
  IntroducingBrokerRebateGroup,
} from 'api/Trading-Accounts/Types/IntroducingBrokerRebates'
import { IntroducingBrokerReferralRestrictionDto } from 'api/Trading-Accounts/Types/IntroducingBrokerReferralRestrictionDto'
import { IntroducingBrokerStatus } from 'api/Trading-Accounts/Types/IntroducingBrokerStatus'
import { PlatformTypeDto } from 'api/Trading-Accounts/Types/PlatformTypeDto'
import { TickmillCompanyAllowedReferralsDto } from 'api/Trading-Accounts/Types/TickmillCompanyAllowedReferralsDto'
import { TradingAccountClientDto } from 'api/Trading-Accounts/Types/TradingAccountClientDto'
import { UpdateIntroducingBroker } from 'api/Trading-Accounts/Types/UpdateIntroducingBroker'
import {
  UpdateIntroducingBrokerProfileAccountGroups,
  UpdateIntroducingBrokerProfileBase,
} from 'api/Trading-Accounts/Types/UpdateIntroducingBrokerProfile'
import { AddRolePermissionFormValues } from 'routes/Administration/Roles/EditRolePermissionForm'
import { AddEditIBEntityFormValues } from 'routes/Brokerage/ReferralsRestrictions/AddEditIBEntityForm'
import { AddEditIBReferralRestrictionFormValues } from 'routes/Records/IB/AddEditIBReferralRestrictionForm'

import { ApiClient } from './ApiClient'
import { PageQuery, preparePageQueryParams } from './index'

export class PartnershipsApiClient {
  constructor(private apiClient: ApiClient) {}

  getIntroducingBrokerReferralExceptions: (
    introducingBrokerId: string
  ) => Promise<IntroducingBrokerReferralRestrictionDto[]> = (introducingBrokerId) => {
    return this.apiClient.get(
      `partnerships/introducing-brokers/${introducingBrokerId}/referral-exceptions`
    )
  }

  getIntroducingBrokerRebates: (
    id: string,
    query?: PageQuery
  ) => Promise<PageData<IntroducingBrokerRebate[]>> = (id, query) => {
    return this.apiClient.get(
      `partnerships/introducing-brokers/${id}/trading-accounts/rebates?${preparePageQueryParams(
        query
      )}`
    )
  }

  getIntroducingBrokerRebatesEvents: (id: string) => Promise<AuditLog[]> = (id) => {
    return this.apiClient.get(`partnerships/introducing-brokers/${id}/rebates/events`)
  }

  getIntroducingBrokerRebatePoliciesEvents: (id: string) => Promise<AuditLog[]> = (id) => {
    return this.apiClient.get(`partnerships/introducing-brokers/rebate-policies/${id}/events`)
  }

  getIntroducingBrokerIBRebateSettingsEvents: (id: string) => Promise<AuditLog[]> = (id) => {
    return this.apiClient.get(`partnerships/introducing-brokers/ib-rebate-settings/${id}/events`)
  }

  getIntroducingBrokerRebateGroups: (
    id: string,
    query?: PageQuery
  ) => Promise<PageData<IntroducingBrokerRebateGroup[]>> = (id, query) => {
    return this.apiClient.get(
      `partnerships/introducing-brokers/${id}/rebate-groups?${preparePageQueryParams(query)}`
    )
  }

  getIBRebatesApprovalTypes: () => Promise<NameDto[]> = () => {
    return this.apiClient.get(`partnerships/introducing-brokers/rebate-approval-types`)
  }

  getIBRebatesPeriods: () => Promise<NameDto[]> = () => {
    return this.apiClient.get(`partnerships/introducing-brokers/rebate-periods`)
  }

  createIBIndividaulRebate: (ibID: string, data: IBIndividualRebate) => Promise<void> = (
    ibID,
    data
  ) => {
    return this.apiClient.post(
      `partnerships/introducing-brokers/${ibID}/trading-accounts/rebates`,
      data
    )
  }

  addIBTAToGroup: (ibID: string, taIDs: string[], groupID: string) => Promise<void> = (
    ibID,
    taIDs,
    groupID
  ) => {
    return this.apiClient.post(
      `partnerships/introducing-brokers/${ibID}/trading-accounts/rebate-groups`,
      {
        GroupId: groupID,
        tradingAccountIds: taIDs,
      }
    )
  }

  createRebateGroup: (ibID: string, data: CreateRebateGroup) => Promise<void> = (ibID, data) => {
    return this.apiClient.post(`partnerships/introducing-brokers/${ibID}/rebate-groups`, data)
  }

  updateRebateGroup: (ibID: string, groupId: string, data: CreateRebateGroup) => Promise<void> = (
    ibID,
    groupId,
    data
  ) => {
    return this.apiClient.put(
      `partnerships/introducing-brokers/${ibID}/rebate-groups/${groupId}`,
      data
    )
  }

  deleteRebateGroups: (ibID: string, groupIds: string[]) => Promise<void> = (ibID, groupIds) => {
    return this.apiClient.delete(`partnerships/introducing-brokers/${ibID}/rebate-groups`, {
      ids: groupIds,
    })
  }

  removeIBTAsFromGroup: (ibID: string, taIDs: string[], groupID: string) => Promise<void> = (
    ibID,
    taID,
    groupID
  ) => {
    return this.apiClient.delete(
      `partnerships/introducing-brokers/${ibID}/trading-accounts/rebate-groups`,
      {
        tradingAccountIds: taID,
        GroupId: groupID,
      }
    )
  }

  updateIBIndividualRebate: (
    ibID: string,
    tradingAccountId: string,
    data: IBIndividualRebateData
  ) => Promise<void> = (ibID, tradingAccountId, data) => {
    return this.apiClient.put(
      `partnerships/introducing-brokers/${ibID}/trading-accounts/${tradingAccountId}/rebates`,
      data
    )
  }

  deleteIBIndividualRebate: (ibID: string, tradingAccountId: string) => Promise<void> = (
    ibID,
    tradingAccountId
  ) => {
    return this.apiClient.delete(
      `partnerships/introducing-brokers/${ibID}/trading-accounts/${tradingAccountId}/rebates`
    )
  }

  addIntroducingBrokerReferralException: (
    introducingBrokerId: string,
    data: AddEditIBReferralRestrictionFormValues
  ) => Promise<void> = (introducingBrokerId, data) => {
    return this.apiClient.post(
      `partnerships/introducing-brokers/${introducingBrokerId}/referral-exceptions`,
      data
    )
  }

  deleteIntroducingBrokerReferralException: (
    introducingBrokerId: string,
    id: number
  ) => Promise<void> = (introducingBrokerId, id) => {
    return this.apiClient.delete(
      `partnerships/introducing-brokers/${introducingBrokerId}/referral-exceptions/${id}`
    )
  }

  getMultiTierNetwork: (
    introducingBrokerId: string,
    query?: PageQuery
  ) => Promise<PageData<MultiTierNetworkDto[]>> = (introducingBrokerId, query) => {
    return this.apiClient.get(
      `partnerships/introducing-brokers/${introducingBrokerId}/multi-tier-network?${preparePageQueryParams(
        query
      )}`
    )
  }

  getIntroducingBrokers: (query?: PageQuery) => Promise<PageData<IntroducingBrokerDto[]>> = (
    query
  ) => {
    return this.apiClient.get(`partnerships/introducing-brokers?${preparePageQueryParams(query)}`)
  }

  getIntroducingBrokerById: (id: string) => Promise<IntroducingBrokerDetailsDto> = (id) => {
    return this.apiClient.get(`partnerships/introducing-brokers/${id}`)
  }

  getIntroducingBrokerLoyalty: (id: string) => Promise<IntroducingBrokerLoyaltyDto> = (id) => {
    return this.apiClient.get(`partnerships/introducing-brokers/${id}/loyalty`)
  }

  getIntroducingBrokersProfiles: (
    query?: PageQuery
  ) => Promise<PageData<IntroducingBrokerProfileDto[]>> = (query) => {
    return this.apiClient.get(
      `partnerships/introducing-brokers/profiles?${preparePageQueryParams(query)}`
    )
  }

  getIntroducingBrokersCompanyReferrals: (
    query?: PageQuery
  ) => Promise<PageData<TickmillCompanyAllowedReferralsDto[]>> = (query) => {
    return this.apiClient.get(
      `partnerships/introducing-brokers/company-referrals?${preparePageQueryParams(query)}`
    )
  }

  addIntroducingBrokersCompanyReferral: (data: AddEditIBEntityFormValues) => Promise<void> = (
    data
  ) => {
    return this.apiClient.post(`partnerships/introducing-brokers/company-referrals`, data)
  }

  deleteIntroducingBrokersCompanyReferral: (
    tickmillCompany: number,
    allowedCompanyId: number
  ) => Promise<void> = (tickmillCompany, allowedCompanyId) => {
    return this.apiClient.delete(
      `partnerships/introducing-brokers/company-referrals/${tickmillCompany}/${allowedCompanyId}`
    )
  }

  getIntroducingBrokerProfileById: (id: string) => Promise<IntroducingBrokerDetailedProfileDto> = (
    id
  ) => {
    return this.apiClient.get(`partnerships/introducing-brokers/profiles/${id}`)
  }

  getIntroducingBrokerProfileByIdUploadStandardCommissions: (id: string) => Promise<BlobPart> = (
    id
  ) => {
    return this.apiClient.get(
      `partnerships/introducing-brokers/profiles/${id}/upload-standard-commissions`
    )
  }

  uploadIntroducingBrokerProfileByIdUploadStandardCommissions: (
    profileId: string,
    data: FormData
  ) => Promise<void> = (profileId, data) => {
    return this.apiClient.upload(
      'PUT',
      `partnerships/introducing-brokers/profiles/${profileId}/upload-standard-commissions`,
      data
    )
  }

  getIntroducingBrokerProfileIBs: (id: string) => Promise<IntroducingBroker[]> = (id) => {
    return this.apiClient.get(`partnerships/introducing-brokers/profiles/${id}/introducing-brokers`)
  }

  getIntroducingBrokerProfileInstruments: (id: string) => Promise<Instrument[]> = (id) => {
    return this.apiClient.get(`partnerships/introducing-brokers/profiles/${id}/instruments`)
  }

  createIntroducingBrokerProfile: (data: CreateIntroducingBrokerProfile) => Promise<void> = (
    data
  ) => {
    return this.apiClient.post(`partnerships/introducing-brokers/profiles`, data)
  }

  updateIntroducingBrokerProfile: (data: UpdateIntroducingBrokerProfileBase) => Promise<void> = (
    data
  ) => {
    return this.apiClient.put(`partnerships/introducing-brokers/profiles/${data.id}`, data)
  }

  cloneIntroducingBrokerProfile: (introducingBrokerProfileId: string) => Promise<void> = (
    introducingBrokerProfileId
  ) => {
    return this.apiClient.post(
      `partnerships/introducing-brokers/profiles/${introducingBrokerProfileId}/clone`,
      {}
    )
  }

  getIntroducingBrokerProfileEvents: (id: string) => Promise<AuditLog[]> = (id) => {
    return this.apiClient.get(`partnerships/introducing-brokers/profiles/${id}/events`)
  }

  updateIntroducingBrokerProfileAccountGroups: (
    id: string,
    data: UpdateIntroducingBrokerProfileAccountGroups
  ) => Promise<void> = (id, data) => {
    return this.apiClient.put(
      `partnerships/introducing-brokers/profiles/${id}/account-groups`,
      data
    )
  }

  getTradingAccountsByIntroducingBrokerId: (
    introducingBrokerId: string,
    query?: PageQuery
  ) => Promise<PageData<TradingAccountDto[]>> = (introducingBrokerId, query) => {
    return this.apiClient.get(
      `partnerships/introducing-brokers/${introducingBrokerId}/trading-accounts?${preparePageQueryParams(
        query
      )}`
    )
  }

  updateIntroducingBroker: (data: UpdateIntroducingBroker) => Promise<void> = (data) => {
    return this.apiClient.put(`partnerships/introducing-brokers/${data.id}`, data)
  }

  updateIntroducingBrokerBonusNv: (data: AdjustNotionalValue) => Promise<void> = (data) => {
    return this.apiClient.put('/partnerships/introducing-brokers/adjust-bonus-nv', data)
  }

  getClientsForIntroducingBroker: (
    introducingBrokerId: string,
    query: string | null,
    pageIndex?: number,
    pageSize?: number
  ) => Promise<PageData<TradingAccountClientDto[]>> = (
    introducingBrokerId,
    query,
    pageIndex = 1,
    pageSize = 20
  ) => {
    let url
    if (!query) {
      url = `partnerships/introducing-brokers/${introducingBrokerId}/clients?pageIndex=${pageIndex}&pageSize=${pageSize}`
    } else {
      url = `partnerships/introducing-brokers/${introducingBrokerId}/clients?Search=${query}&pageIndex=${pageIndex}&pageSize=${pageSize}`
    }

    return this.apiClient.get(url)
  }

  getIntroducingBrokerAuditLogs: (id: string) => Promise<AuditLog[]> = (id) => {
    return this.apiClient.get(`partnerships/introducing-brokers/${id}/events`)
  }

  getAssignedIntroducingBrokerCommissionPlanGroups: (
    introducingBrokerId: string
  ) => Promise<IntroducingBrokerCommissionPlanGroupDto> = (introducingBrokerId) => {
    return this.apiClient.get(
      `partnerships/introducing-brokers/${introducingBrokerId}/commission-plan-groups/assigned`
    )
  }

  getIntroducingBrokerMultiTierNetworkDetails: (
    introducingBrokerId: string
  ) => Promise<IntroducingBrokerMultiTierDetails> = (introducingBrokerId) => {
    return this.apiClient.get(
      `partnerships/introducing-brokers/multi-tier-network/${introducingBrokerId}/account-details`
    )
  }

  getIntroducingBrokerCommissionPlanGroups: (
    introducingBrokerId: string
  ) => Promise<IntroducingBrokerCommissionPlanGroupDto[]> = (introducingBrokerId) => {
    return this.apiClient.get(
      `partnerships/introducing-brokers/${introducingBrokerId}/commission-plan-groups`
    )
  }

  updateIntroducingBrokerCommissionPlanGroups: (
    introducingBrokerId: string,
    commissionPlanGroupId: string,
    data: AddEditIntroducingBrokerCommissionPlanGroupDtoFormValues[]
  ) => Promise<void> = (introducingBrokerId, commissionPlanGroupId, data) => {
    return this.apiClient.put(
      `partnerships/introducing-brokers/${introducingBrokerId}/commission-plan-groups/${commissionPlanGroupId}`,
      { commissionPlans: data }
    )
  }

  updateIntroducingBrokerSubAccounts: (
    introducingBrokerId: string,
    data: UpdateIntroducingBrokerMultiTierSubAccountFormValues
  ) => Promise<void> = (introducingBrokerId, data) => {
    return this.apiClient.put(
      `partnerships/introducing-brokers/multi-tier-network/sub-accounts/${introducingBrokerId}`,
      data
    )
  }

  addIntroducingBrokerCommissionPlanGroups: (
    introducingBrokerId: string,
    data: AddEditIntroducingBrokerCommissionPlanGroupDtoFormValues[]
  ) => Promise<void> = (introducingBrokerId, data) => {
    return this.apiClient.post(
      `partnerships/introducing-brokers/${introducingBrokerId}/commission-plan-groups`,
      {
        commissionPlans: data,
      }
    )
  }

  getIntroducingBrokersByTickmillCompany: (id: number) => Promise<IntroducingBrokerBasicDto[]> = (
    id
  ) => {
    return this.apiClient.get(`partnerships/introducing-brokers/basic?tickmillCompanyId=${id}`)
  }

  getIntroducingBrokerProfileByTickmillCompanyId: (
    id: number
  ) => Promise<PageData<IntroducingBrokerProfileDto[]>> = (id) => {
    return this.apiClient.get(
      `partnerships/introducing-brokers/profiles?Search=TickmillCompanyId == ${id}&PageIndex=${1}&PageSize=${1000}`
    )
  }

  getIntroducingBrokerStatus: () => Promise<IntroducingBrokerStatus[]> = () => {
    return this.apiClient.get(`partnerships/introducing-brokers/statuses`)
  }

  getIntroducingBrokerCalculationStatus: () => Promise<IntroducingBrokerCalculationStatus[]> =
    () => {
      return this.apiClient.get(`partnerships/introducing-brokers/commission-calculation-statuses`)
    }

  getIntroducingBrokerProfileAccountType: () => Promise<IntroducingBrokerProfileAccountType[]> =
    () => {
      return this.apiClient.get(`partnerships/introducing-brokers/profiles/account-types`)
    }

  getIntroducingBrokerParentTypes: () => Promise<NameDto[]> = () => {
    return this.apiClient.get(`partnerships/introducing-brokers/parent-types`)
  }

  massAssignUserToIbs: ({
    userId,
    ibIds,
    criteria,
  }: {
    userId: string
    ibIds?: string[]
    criteria?: PageQuery
  }) => Promise<void> = ({ userId, ibIds, criteria }) => {
    return this.apiClient.put(`partnerships/introducing-brokers/mass-assign`, {
      userId,
      ibIds,
      criteria,
    })
  }

  massUnAssignUsersFromIbs: (ibIds: string[]) => Promise<void> = (ibIds) => {
    return this.apiClient.put(`partnerships/introducing-brokers/mass-unassign`, { ibIds })
  }

  getBonusNvAdjustmentReasons: () => Promise<NameDto[]> = () => {
    return this.apiClient.get(`partnerships/introducing-brokers/bonus-nv-adjustment-reasons`)
  }

  getRebateCalcMethodType: () => Promise<PlatformTypeDto[]> = () => {
    return this.apiClient.get(`partnerships/introducing-brokers/rebate-calculation-methods`)
  }

  getDeals: (query?: PageQuery) => Promise<PageData<DealDto[]>> = (query) => {
    return this.apiClient.get(`partnerships/deals?${preparePageQueryParams(query)}`)
  }

  getPartnershipAccountsSecuredPropertiesLabels: () => Promise<SecuredPropertiesLabels[]> = () => {
    return this.apiClient.get(`partnerships/secured-properties/labels`)
  }

  getRolePartnershipsSecuredPropertiesLabels: (
    query?: PageQuery
  ) => Promise<RoleSecuredProperties[]> = (query) => {
    return this.apiClient.get(`partnerships/secured-properties?${preparePageQueryParams(query)}`)
  }

  setRolePartnershipsSecuredProperties: (
    data: AddRolePermissionFormValues
  ) => Promise<SecuredPropertiesLabels[]> = (data) => {
    return this.apiClient.post(`partnerships/secured-properties/set`, data)
  }

  deleteRolePartnershipsSecuredProperties: (
    data: Omit<AddRolePermissionFormValues, 'securedProperties'> & {
      name: string
    }
  ) => Promise<SecuredPropertiesLabels[]> = ({ aggregateType, role, name }) => {
    return this.apiClient.delete(`partnerships/secured-properties/${aggregateType}/${role}/${name}`)
  }

  getProfileStandardCommissions: (
    id: string,
    query?: PageQuery
  ) => Promise<PageData<StandardCommission[]>> = (id: string, query?: PageQuery) => {
    return this.apiClient.get(
      `partnerships/introducing-brokers/profiles/${id}/standard-commissions?${preparePageQueryParams(
        query
      )}`
    )
  }

  updateIntroducingBrokerProfileStandardCommissions: (
    id: string,
    data: { StandardCommissions: StandardCommissionDto[] }
  ) => Promise<void> = (id, data) => {
    return this.apiClient.put(
      `partnerships/introducing-brokers/profiles/${id}/standard-commissions`,
      data
    )
  }

  getIntroducingBrokerInstrumentCommissions: (
    id: string,
    query?: PageQuery
  ) => Promise<PageData<StandardCommission[]>> = (id: string, query?: PageQuery) => {
    return this.apiClient.get(
      `partnerships/introducing-brokers/${id}/instrument-commissions?${preparePageQueryParams(
        query
      )}`
    )
  }

  updateIntroducingBrokerInstrumentCommissions: (
    id: string,
    data: { InstrumentCommissions: StandardCommissionDto[] }
  ) => Promise<void> = (id, data) => {
    return this.apiClient.put(`partnerships/introducing-brokers/${id}/instrument-commissions`, data)
  }

  getRebatePolicies: (query?: PageQuery) => Promise<PageData<RebatePoliciesDto[]>> = (query) => {
    return this.apiClient.get(
      `partnerships/introducing-brokers/rebate-policies?${preparePageQueryParams(query)}`
    )
  }

  createRebatePolicies: (data: CreateRebatePolicies) => Promise<void> = (data) => {
    return this.apiClient.post(`partnerships/introducing-brokers/rebate-policies`, data)
  }

  deleteRebatePolicies: (rebatePoliciesId: string) => Promise<void> = (rebatePoliciesId) => {
    return this.apiClient.delete(
      `partnerships/introducing-brokers/rebate-policies/${rebatePoliciesId}`
    )
  }

  updateRebatePolicies: (rebatePoliciesId: string, data: CreateRebatePolicies) => Promise<void> = (
    rebatePoliciesId,
    data
  ) => {
    return this.apiClient.put(
      `partnerships/introducing-brokers/rebate-policies/${rebatePoliciesId}`,
      data
    )
  }

  getRebateSettings: (query?: PageQuery) => Promise<PageData<RebateSettingsDto[]>> = (query) => {
    return this.apiClient.get(
      `partnerships/introducing-brokers/ib-rebate-settings?${preparePageQueryParams(query)}`
    )
  }

  createRebateSettings: (data: CreateRebateSettings) => Promise<void> = (data) => {
    return this.apiClient.post(`partnerships/introducing-brokers/ib-rebate-settings`, data)
  }

  deleteRebateSettings: (rebateSettingsId: string) => Promise<void> = (rebateSettingsId) => {
    return this.apiClient.delete(
      `partnerships/introducing-brokers/ib-rebate-settings/${rebateSettingsId}`
    )
  }

  updateRebateSettings: (rebateSettingsId: string, data: CreateRebateSettings) => Promise<void> = (
    rebateSettingsId,
    data
  ) => {
    return this.apiClient.put(
      `partnerships/introducing-brokers/ib-rebate-settings/${rebateSettingsId}`,
      data
    )
  }
}
